/* eslint-disable max-len */
import React, { useState, useEffect, useCallback } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Card, CardTitle, Typography, Alert } from '@one-thd/sui-atomic-components';
import { useConfigService } from '@thd-nucleus/experience-context';
import { Configurator } from '@gcc/configurator';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import Banner from '../components/Banner';
import getConfiguratorSettings from '../utils/getConfiguratorSettings';
import { findTaggedValue } from '../utils/tags';
import Layout from '../configuratorTemplates/Layout';
import { getStore } from '../store';
import HowItWorksDrawer from '../components/HowItWorksDrawer';
import ProjectLocation from '../components/ProjectLocation';
import { totalProjectPrice } from '../selectors/pricing';
import { selectSelectedSkuData } from '../selectors/skuDataSelectors';
import {
  ConfiguratorWrapperPropTypes,
  defaultConfiguratorWrapperPropTypes
} from './_propTypes';
import { setOmsIdFromUrl, setSkuData, setPageError } from '../actions';
import { useIsMobile } from '../components/ProjectLocation/helpers';
import ProjectSummary from '../components/projectSummary/index';
import SummaryDrawer from '../components/SummaryDrawer';
import { canContinueSelector } from '../selectors/steps/steps';
import {
  useConfiguratorExtraSelections,
  useConfiguratorAdditionalDetails,
  useConfiguratorLoadFailure,
} from './customHooks';
import ErrorOnPage from '../components/ErrorOnPage';
import { ExperienceTitle } from '../components/ExperienceTitle.component';
import { FlooringSkeleton } from '../components/FlooringSkeleton.component';

const ConfiguratorWrapper = ({
  /* passed props from AppInner */
  afterRulesExecuted,
  onConfiguratorLoadSuccess,
  isFromEdit,
  isFromSave,
  onChoiceSelectedSuccess,
  onSaveConfiguration,
  productId,
  omsId,
  /* passed props from state connect */
  config,
  storeNumber,
  isInitialized,
  currentItem: { itemId, itemProduct },
  currentDesignId,
  selectedChoice,
}) => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const [bannerMessage, setBannerMessage] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  const [loadConfigurator, setLoadConfigurator] = useState(!isFromEdit);
  const [errorCode, setErrorCode] = useState('missingContent');

  const canContinue = useSelector(canContinueSelector);
  const carpetPrice = useSelector(totalProjectPrice) || 0;
  const selectedSkuData = useSelector(selectSelectedSkuData);
  const { omsIdFromUrl } = useSelector((state) => state.mandiMore);
  const { hasErrors, errors } = useSelector((state) => state.errorsOnPage);
  const configurationId = get(itemProduct, 'configurationGuid');
  const experienceTitle = useConfigService('experience-title');

  const extraSelections = useConfiguratorExtraSelections();
  const configuratorAdditionalDetails = useConfiguratorAdditionalDetails();
  const configuratorFailedToLoad = useConfiguratorLoadFailure();

  useEffect(() => {
    setLoadConfigurator(!isFromEdit || (isFromEdit && currentDesignId));
  }, [currentDesignId]);

  useEffect(() => {
    if (configuratorFailedToLoad) {
      dispatch(setPageError({ errorMsg: 'Configurator Load Failed for selected omsId' }));
      setErrorCode('invalidContent');
    }
  }, [configuratorFailedToLoad]);

  const configSettings = {
    afterRulesExecuted,
    onConfiguratorLoadSuccess,
    isFromEdit,
    isFromSave,
    onChoiceSelectedSuccess,
    onSaveConfiguration,
    productId,
    omsId,
    config,
    storeNumber,
    itemId,
    designId: currentDesignId,
    selectedChoice,
    // not from props
    totalPrice: carpetPrice,
    configId: configurationId,
    extraSelections,
    configuratorAdditionalDetails,
    mainImgUrl: selectedSkuData?.primaryImageUrl?.replace('<SIZE>', '100') || selectedChoice?.mediaUrl,
    setSkuDataAction: (data) => dispatch(setSkuData(data))
  };
  const settings = getConfiguratorSettings({ ...configSettings });

  useEffect(() => {
    if (!isEmpty(selectedChoice)) {
      const { value, tags } = selectedChoice;
      const collection = findTaggedValue(tags, 'collection');
      const selection = (<strong>{collection} - {value}</strong>);
      if (!selectedChoice?.isAvailable) {
        setBannerMessage({
          hasTimer: false,
          message: <>Your selected carpet:&nbsp;{selection}&nbsp;is unavailable at this time.  Please update your selections below to continue.</>
        });
      } else {
        setBannerMessage({
          hasTimer: true,
          message: <>Your carpet color has been carried over from the previous page:&nbsp;{selection}. Please enter room information</>,
        });
      }
    }
  }, [selectedChoice]);

  useEffect(() => {
    const showBannerCriteria = (omsIdFromUrl ||
      (!omsIdFromUrl && !selectedChoice?.isAvailable)) &&
      !isEmpty(bannerMessage) && !isFromEdit;
    setShowBanner(showBannerCriteria);
  }, [omsIdFromUrl, bannerMessage, selectedChoice]);

  const handleCloseBanner = useCallback(() => {
    dispatch(setOmsIdFromUrl(null));
  });

  // if isFromEdit we want to prevent Configurator from loading until we have the configurationId
  return (
    <>
      <div className="flooring-container">
        {hasErrors && <ErrorOnPage errorCode={errorCode} />}
        <div className="flooring-wrapper" data-name="FlooringWrapper">
          <div className="configurator-container" data-name="ConfiguratorContainer">
            <Banner
              bannerMessage={bannerMessage?.message}
              hasTimer={bannerMessage?.hasTimer}
              showBanner={showBanner}
              closeBannerCb={handleCloseBanner}
            />
            <ExperienceTitle />
            <Card style={{ marginBottom: '10px', border: 'none', height: 'auto' }} >
              <CardTitle
                data-testid="project-locations"
                header={(
                  <Typography variant="h3" weight="bold">
                    Your Preferred Store
                  </Typography>
                )}
              />
              <ProjectLocation />
            </Card>
            {loadConfigurator && (
              <div data-name="configurator-layout-wrapper">
                <Configurator settings={settings} store={getStore()}>
                  <Layout />
                </Configurator>
              </div>
            )}
          </div>
          {!isMobile && <ProjectSummary canContinue={canContinue} />}
        </div>
      </div>
      <SummaryDrawer canContinue={canContinue} />
    </>
  );
};

ConfiguratorWrapper.propTypes = ConfiguratorWrapperPropTypes;
ConfiguratorWrapper.defaultProps = defaultConfiguratorWrapperPropTypes;

const mapStateToProps = (state) => {
  const {
    configurator: {
      ajaxStatus: { isInitialized, isLoading: configuratorIsLoading }
    },
    config,
    design: { currentItem, isSaving },
    store: { storeNumber },
    pricing: { selectedChoice }
  } = state;

  return {
    config,
    storeNumber,
    isInitialized,
    currentItem,
    currentDesignId: state.design.currentDesign.designId,
    selectedChoice,
    configuratorIsLoading,
    isSaving
  };
};

export default connect(mapStateToProps)(ConfiguratorWrapper);
